import { DatePipe } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { ProcessTask, WorkflowApiService } from './workflow-api.service';
import { bindWorkflowTaskLiveUpdates } from './workflow-live-updates';

@Component({
  standalone: true,
  selector: 'ain-workflow-tasks',
  imports: [
    AinPageComponent,
    AinStateComponent,
    FormsModule,
    TableModule,
    RouterLink,
    TagModule,
    InputTextModule,
    SelectModule,
    ButtonModule,
    DatePipe
  ],
  template: `
    <ain-page [title]="t('workflow.tasks.title')" [description]="t('workflow.tasks.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="error() ?? ''" />
      } @else {
        <section class="tasks-shell">
          <div class="kpis">
            <article class="kpi-card">
              <span class="kpi-label">{{ t('workflow.tasks.kpi.total') }}</span>
              <strong class="kpi-value">{{ allTasks().length }}</strong>
            </article>
            <article class="kpi-card warning">
              <span class="kpi-label">{{ t('workflow.tasks.kpi.overdue') }}</span>
              <strong class="kpi-value">{{ overdueCount() }}</strong>
            </article>
            <article class="kpi-card success">
              <span class="kpi-label">{{ t('workflow.tasks.kpi.withDueDate') }}</span>
              <strong class="kpi-value">{{ withDueDateCount() }}</strong>
            </article>
          </div>

          <div class="filters">
            <span class="filter-full">
              <input
                pInputText
                [(ngModel)]="query"
                (ngModelChange)="onFilterChanged()"
                [placeholder]="t('workflow.tasks.filters.searchPlaceholder')" />
            </span>

            <p-select
              [options]="statusOptions"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="statusFilter"
              (ngModelChange)="onFilterChanged()"
              [placeholder]="t('workflow.tasks.filters.status')" />

            <p-select
              [options]="dueOptions"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="dueFilter"
              (ngModelChange)="onFilterChanged()"
              [placeholder]="t('workflow.tasks.filters.due')" />

            <label class="date-filter">
              <span>{{ t('workflow.tasks.filters.createdFrom') }}</span>
              <input type="date" [(ngModel)]="createdFrom" (ngModelChange)="onFilterChanged()" />
            </label>

            <label class="date-filter">
              <span>{{ t('workflow.tasks.filters.createdTo') }}</span>
              <input type="date" [(ngModel)]="createdTo" (ngModelChange)="onFilterChanged()" />
            </label>

            <div class="filter-actions">
              <button pButton type="button" (click)="reload(true)" [label]="t('common.refresh')" outlined></button>
              <button pButton type="button" (click)="resetFilters()" [label]="t('workflow.tasks.filters.reset')"></button>
            </div>
          </div>
        </section>

        <p-table [value]="filteredTasks()" [paginator]="true" [rows]="10" [rowsPerPageOptions]="[10,20,50]">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('columns.name') }}</th>
              <th>{{ t('columns.status') }}</th>
              <th>{{ t('workflow.taskHost.operatorType') }}</th>
              <th>{{ t('workflow.tasks.columns.createdAt') }}</th>
              <th>{{ t('workflow.tasks.columns.dueDate') }}</th>
              <th>{{ t('workflow.tasks.columns.remaining') }}</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td class="task-name">
                <span>{{ row.name }}</span>
                <small class="task-id">{{ row.bpmnElementId }}</small>
              </td>
              <td><p-tag [value]="row.status" [severity]="statusSeverity(row.status)" /></td>
              <td>
                @if (row.operatorType || row.surfaceKey) {
                  <p-tag
                    [value]="operatorLabel(row)"
                    [severity]="row.operatorType === 'ModuleSurface' ? 'info' : 'secondary'" />
                }
              </td>
              <td>{{ row.createdAtUtc | date: 'short' }}</td>
              <td>
                @if (row.dueDateUtc) {
                  {{ row.dueDateUtc | date: 'short' }}
                } @else {
                  <span class="muted">{{ t('workflow.tasks.noDueDate') }}</span>
                }
              </td>
              <td>
                @if (row.dueDateUtc) {
                  <p-tag [value]="remainingLabel(row)" [severity]="dueSeverity(row)" />
                } @else {
                  <span class="muted">-</span>
                }
              </td>
              <td><a [routerLink]="['/workflow/tasks', row.id]">{{ t('common.open') }}</a></td>
            </tr>
          </ng-template>
        </p-table>
      }
    </ain-page>
  `,
  styles: [`
    a { color: var(--ain-color-primary); font-weight: 600; }
    .tasks-shell { margin-bottom: 16px; }
    .kpis {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 10px;
      margin-bottom: 12px;
    }
    .kpi-card {
      background: var(--ain-color-surface);
      border: 1px solid var(--ain-color-border, #e5e7eb);
      border-radius: 12px;
      padding: 12px;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .kpi-card.warning { border-inline-start: 4px solid #f59e0b; }
    .kpi-card.success { border-inline-start: 4px solid #22c55e; }
    .kpi-label { font-size: 0.8rem; color: var(--ain-color-text-muted, #64748b); }
    .kpi-value { font-size: 1.2rem; }

    .filters {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 10px;
      align-items: end;
      background: var(--ain-color-surface);
      border: 1px solid var(--ain-color-border, #e5e7eb);
      border-radius: 12px;
      padding: 12px;
    }
    .filter-full { grid-column: 1 / -1; }
    .date-filter { display: flex; flex-direction: column; gap: 6px; font-size: 0.8rem; color: var(--ain-color-text-muted, #64748b); }
    .date-filter input {
      border: 1px solid var(--ain-color-border, #cbd5e1);
      border-radius: 8px;
      padding: 0.55rem 0.65rem;
      font: inherit;
      background: white;
    }
    .filter-actions { display: flex; gap: 8px; align-items: center; }
    .task-name { display: flex; flex-direction: column; gap: 2px; }
    .task-id { color: var(--ain-color-text-muted, #64748b); font-size: 0.78rem; }
    .muted { color: var(--ain-color-text-muted, #64748b); }
  `]
})
export class WorkflowTasksComponent implements OnInit {
  private readonly api = inject(WorkflowApiService);
  private readonly localization = inject(LocalizationService);
  private readonly liveUpdateService = inject(LiveUpdateService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);

  readonly allTasks = signal<ProcessTask[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly overdueCount = computed(() => this.allTasks().filter(item => this.isOverdue(item)).length);
  readonly withDueDateCount = computed(() => this.allTasks().filter(item => !!item.dueDateUtc).length);
  readonly filteredTasks = computed(() => this.applyFilters(this.allTasks()));

  query = '';
  statusFilter = '';
  dueFilter: 'all' | 'overdue' | 'today' | 'thisWeek' | 'noDueDate' = 'all';
  createdFrom = '';
  createdTo = '';

  ngOnInit(): void {
    this.reload(true);
    bindWorkflowTaskLiveUpdates(
      {
        liveUpdateService: this.liveUpdateService,
        contextService: this.contextService,
        destroyRef: this.destroyRef
      },
      showLoading => this.reload(showLoading)
    );
  }

  operatorLabel(row: ProcessTask): string {
    if (row.surfaceKey) {
      return row.surfaceKey;
    }

    return row.operatorType ?? 'UserTask';
  }

  get statusOptions(): { label: string; value: string }[] {
    const statuses = Array.from(new Set(this.allTasks().map(item => item.status))).sort((a, b) => a.localeCompare(b));
    return [
      { label: this.t('common.all'), value: '' },
      ...statuses.map(status => ({ label: status, value: status }))
    ];
  }

  get dueOptions(): { label: string; value: 'all' | 'overdue' | 'today' | 'thisWeek' | 'noDueDate' }[] {
    return [
      { label: this.t('common.all'), value: 'all' },
      { label: this.t('workflow.tasks.filters.overdue'), value: 'overdue' },
      { label: this.t('workflow.tasks.filters.today'), value: 'today' },
      { label: this.t('workflow.tasks.filters.thisWeek'), value: 'thisWeek' },
      { label: this.t('workflow.tasks.filters.noDueDate'), value: 'noDueDate' }
    ];
  }

  onFilterChanged(): void {
    // Signal-based computed filtering is reactive on model changes.
  }

  resetFilters(): void {
    this.query = '';
    this.statusFilter = '';
    this.dueFilter = 'all';
    this.createdFrom = '';
    this.createdTo = '';
  }

  statusSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'inprogress':
      case 'claimed':
        return 'info';
      case 'escalated':
      case 'delegated':
        return 'warn';
      case 'cancelled':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  dueSeverity(task: ProcessTask): 'success' | 'warn' | 'danger' {
    if (!task.dueDateUtc) {
      return 'success';
    }

    const now = Date.now();
    const due = new Date(task.dueDateUtc).getTime();
    if (due < now) {
      return 'danger';
    }

    const hoursLeft = (due - now) / (1000 * 60 * 60);
    if (hoursLeft <= 24) {
      return 'warn';
    }

    return 'success';
  }

  remainingLabel(task: ProcessTask): string {
    if (!task.dueDateUtc) {
      return '-';
    }

    const now = Date.now();
    const due = new Date(task.dueDateUtc).getTime();
    const diff = due - now;
    const duration = this.humanizeDuration(Math.abs(diff));
    return diff < 0
      ? `${this.t('workflow.tasks.remaining.overdue')} ${duration}`
      : `${duration} ${this.t('workflow.tasks.remaining.left')}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  reload(showLoading = false): void {
    if (showLoading) {
      this.loading.set(true);
      this.error.set(null);
    }

    this.api.getInbox().subscribe({
      next: items => {
        this.allTasks.set(items);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  private applyFilters(items: ProcessTask[]): ProcessTask[] {
    const query = this.query.trim().toLowerCase();
    const from = this.createdFrom ? new Date(`${this.createdFrom}T00:00:00`).getTime() : null;
    const to = this.createdTo ? new Date(`${this.createdTo}T23:59:59`).getTime() : null;
    const now = Date.now();
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date(todayStart);
    todayEnd.setDate(todayEnd.getDate() + 1);
    const weekEnd = now + (7 * 24 * 60 * 60 * 1000);

    return items
      .filter(item => {
        if (query) {
          const haystack = `${item.name} ${item.status} ${item.bpmnElementId} ${item.surfaceKey ?? ''} ${item.operatorType ?? ''}`.toLowerCase();
          if (!haystack.includes(query)) {
            return false;
          }
        }

        if (this.statusFilter && item.status !== this.statusFilter) {
          return false;
        }

        const createdAt = new Date(item.createdAtUtc).getTime();
        if (from !== null && createdAt < from) {
          return false;
        }
        if (to !== null && createdAt > to) {
          return false;
        }

        const due = item.dueDateUtc ? new Date(item.dueDateUtc).getTime() : null;
        switch (this.dueFilter) {
          case 'overdue':
            if (due === null || due >= now) {
              return false;
            }
            break;
          case 'today':
            if (due === null || due < todayStart.getTime() || due >= todayEnd.getTime()) {
              return false;
            }
            break;
          case 'thisWeek':
            if (due === null || due < now || due > weekEnd) {
              return false;
            }
            break;
          case 'noDueDate':
            if (due !== null) {
              return false;
            }
            break;
          default:
            break;
        }

        return true;
      })
      .sort((a, b) => new Date(b.createdAtUtc).getTime() - new Date(a.createdAtUtc).getTime());
  }

  private isOverdue(task: ProcessTask): boolean {
    if (!task.dueDateUtc) {
      return false;
    }

    return new Date(task.dueDateUtc).getTime() < Date.now();
  }

  private humanizeDuration(durationMs: number): string {
    const totalMinutes = Math.max(1, Math.floor(durationMs / (1000 * 60)));
    const days = Math.floor(totalMinutes / (24 * 60));
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const minutes = totalMinutes % 60;

    if (days > 0) {
      return `${days}d ${hours}h`;
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }
}
